import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../data/firebase";
import { suggestTutors } from "../../utils/coverBlocks";
import {
  cancelCoverGroup,
  errorMessage,
  fmtDay,
  fmtRange,
  RESPONSE_LABELS,
  STATUS_LABELS,
  updateCoverGroupInvites,
} from "../../utils/coversApi";

const responseColor = (r) => ({ accepted: "success", declined: "error", pending: "default" }[r] || "default");
const statusColor = (s) => ({ open: "warning", taken: "success" }[s] || "default");

const CoverGroupDetailDialog = ({ groupId, open, onClose, tutors, weekLessons, isManager, currentUserUid }) => {
  const [group, setGroup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      return undefined;
    }
    setError("");
    setConfirmCancel(false);
    setReason("");
    const unsub = onSnapshot(doc(db, "coverGroups", groupId), (snap) => {
      setGroup(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [groupId]);

  const sessions = useMemo(
    () => [...(group?.sessions || [])].sort((a, b) => a.startDateTime.localeCompare(b.startDateTime)),
    [group]
  );
  const invitedIds = useMemo(() => group?.invitedTutorIds || [], [group]);

  const addOptions = useMemo(() => {
    if (!group || !(isManager || group.createdBy === currentUserUid)) return [];
    const lessonLike = sessions.map((s) => ({ ...s, id: s.lessonId }));
    return suggestTutors({
      sessions: lessonLike,
      tutors: tutors.filter((t) => t.role !== "Minion"),
      otherLessons: weekLessons,
      originalTutorIds: group.originalTutorIds || (group.originalTutorId ? [group.originalTutorId] : []),
      hideSensitiveReasons: !isManager,
    }).filter((c) => !invitedIds.includes(c.id));
  }, [group, sessions, tutors, weekLessons, isManager, invitedIds, currentUserUid]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (tutor) =>
    tutor && run(() => updateCoverGroupInvites({ groupId, addTutorIds: [tutor.id] }));
  const handleRemove = (id) => run(() => updateCoverGroupInvites({ groupId, removeTutorIds: [id] }));
  const handleCancel = () =>
    run(async () => {
      await cancelCoverGroup({ groupId, reason });
      setConfirmCancel(false);
    });

  const canEdit = (isManager || group?.createdBy === currentUserUid) && group?.status === "open";

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <span>Cover request</span>
          {group && (
            <Chip size="small" color={statusColor(group.status)} label={STATUS_LABELS[group.status] || group.status} />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!group ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : (
          <Stack gap={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Covering for {group.originalTutorName || "Unassigned"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sent by {group.createdByName}
                {group.message && ` · "${group.message}"`}
              </Typography>
            </Box>

            {group.status === "taken" && (
              <Alert severity="success">
                Taken by <b>{group.acceptedByName}</b>. The sessions have been reassigned.
              </Alert>
            )}
            {group.status === "cancelled" && (
              <Alert severity="info">
                Cancelled by {group.cancelledByName || "senior tutor"}
                {group.cancelReason && `: ${group.cancelReason}`}
              </Alert>
            )}
            {group.status === "expired" && (
              <Alert severity="warning">Expired: the first session has already started.</Alert>
            )}

            <List dense disablePadding>
              {sessions.map((s) => (
                <ListItem key={s.lessonId} disableGutters>
                  <ListItemText
                    primary={`${fmtDay(s.startDateTime)} · ${fmtRange(s.startDateTime, s.endDateTime)} · ${
                      s.subjectGroupName || "Lesson"
                    }${(group.originalTutorIds || []).length > 1 ? ` · ${s.originalTutorName || ""}` : ""}`}
                    secondary={`${(s.studentNames || []).join(", ") || "No students"}${
                      s.locationName ? ` · ${s.locationName}` : ""
                    }`}
                  />
                </ListItem>
              ))}
            </List>

            <Divider />

            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Invited tutors ({invitedIds.length})
              </Typography>
              <List dense disablePadding>
                {invitedIds.map((id) => {
                  const inv = group.invited?.[id] || {};
                  return (
                    <ListItem
                      key={id}
                      disableGutters
                      secondaryAction={
                        canEdit && inv.response !== "accepted" ? (
                          <Tooltip title="Remove from request">
                            <IconButton edge="end" size="small" disabled={busy} onClick={() => handleRemove(id)}>
                              <PersonRemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null
                      }
                    >
                      <ListItemText primary={inv.name || id} />
                      <Chip
                        size="small"
                        sx={{ mr: canEdit ? 5 : 0 }}
                        color={responseColor(inv.response)}
                        label={RESPONSE_LABELS[inv.response] || inv.response}
                      />
                    </ListItem>
                  );
                })}
              </List>

              {canEdit && (
                <Autocomplete
                  size="small"
                  sx={{ mt: 1 }}
                  options={addOptions}
                  getOptionLabel={(o) => o.name}
                  value={null}
                  disabled={busy}
                  onChange={(_, val) => handleAdd(val)}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <Box>
                        <Typography>{o.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.suggested ? "Suggested" : o.reasons.join(" · ")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Add a tutor" />}
                />
              )}
            </Box>

            {confirmCancel && (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Reason (optional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {canEdit &&
          (confirmCancel ? (
            <>
              <Button onClick={() => setConfirmCancel(false)} disabled={busy}>
                Keep open
              </Button>
              <Button color="error" variant="contained" onClick={handleCancel} disabled={busy}>
                Confirm cancel
              </Button>
            </>
          ) : (
            <Button color="error" onClick={() => setConfirmCancel(true)} disabled={busy}>
              Cancel request
            </Button>
          ))}
        <Box flex={1} />
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverGroupDetailDialog;
